import { css } from '@emotion/react'
import Markdown, { Options } from 'markdown-to-jsx'
import { colors, font, mq } from 'plume-ui'
import { desktopMinWidth } from '../../tokens/mediaquery'
import { MarkdownLink } from '../../common/MarkdownLink'
import { isAnchorImage, isImage } from '@/lib/markdown'
import { MarkdownTextDivider } from '../../common/MarkdownTextDivider'
import { MarkdownImage } from '@/components/common/MarkdownImage'

const PADDING_SM = 24
const PADDING_LG = 48

const LIST_MARKER_PADDING = 25

export const paddingSmartphoneStyle = css`
  padding-left: ${PADDING_SM}px;
  padding-right: ${PADDING_SM}px;
`

export const paddingDesktopStyle = css`
  padding-left: ${PADDING_LG}px;
  padding-right: ${PADDING_LG}px;
`

const bodyMdStyle = css`
  & img {
    width: 100vw;
  }

  ${mq.large} {
    & img {
      width: 100%;
    }
  }

  & p {
    font-size: 15px;
    line-height: 1.75;
    color: ${colors.black30};

    ${mq.large} {
      font-size: 16px;
      line-height: 2;
    }
  }

  & h1 {
    line-height: 1.3;
    ${paddingSmartphoneStyle};

    ${mq.large} {
      ${paddingDesktopStyle};
    }
  }

  & h2 {
    font-size: 20px;
    margin-top: 35px;
    line-height: 1.75;
    ${paddingSmartphoneStyle};

    ${mq.large} {
      font-size: 24px;
      margin-top: 80px;
      ${paddingDesktopStyle};
    }
  }

  & h3 {
    font-size: 15px;
    margin: 20px 0;
    line-height: 2;
    ${paddingSmartphoneStyle};

    ${mq.large} {
      font-size: 16px;
      margin-top: 40px;
      ${paddingDesktopStyle};
    }
  }

  & h3:before {
    content: '';
    display: inline-block;
    width: 30px;
    height: 1px;
    margin-right: 8px;
    margin-left: 1px;
    background-color: ${colors.black30};
    vertical-align: 5.5px;
  }

  & h4 {
    line-height: 25px;
    ${paddingSmartphoneStyle};

    ${mq.large} {
      ${paddingDesktopStyle};
    }
  }

  & div + pre {
    margin-top: -15px;
  }
`

type IntroMarkdownProps = {
  children: React.ReactNode
}

export const IntroMarkdown: React.FC<IntroMarkdownProps> = (props) => {
  return (
    <Markdown
      css={css`
        margin-top: 40px;
        margin-bottom: 20px;
        font-size: 15px;
        color: ${colors.black30};
        line-height: 2;
        ${paddingSmartphoneStyle};

        && p {
          line-height: 1.75;
        }

        ${mq.large} {
          font-size: 16px;
          && {
            margin-top: 30px;
            ${paddingDesktopStyle}
          }
          && p {
            line-height: 2;
          }
        }
      `}
      options={{
        forceBlock: true,
        overrides: {
          a: MarkdownLink,
          blockquote: IntroFootnote,
          iframe: IFrame,
          p: PorDiv,
          img: MarkdownImage,
          Image: MarkdownImage,
        },
      }}
      {...props}
    />
  )
}

type BodyMarkdownProps = {
  shrink: boolean
  options?: Options
  children: React.ReactNode
}

export const BodyMarkdown: React.FC<BodyMarkdownProps> = (props) => {
  return (
    <Markdown
      css={css`
        ${bodyMdStyle};
        margin-top: ${props.shrink ? '15px' : '60px'};

        p {
          margin-bottom: 20px;
          ${paddingSmartphoneStyle};

          ${mq.large} {
            ${paddingDesktopStyle};
          }
        }

        ul {
          margin-bottom: 20px;
          padding-left: calc(${PADDING_SM}px + ${LIST_MARKER_PADDING}px);
          padding-right: ${PADDING_SM}px;

          ${mq.large} {
            padding-left: calc(${PADDING_LG}px + ${LIST_MARKER_PADDING}px);
            padding-right: ${PADDING_LG}px;
          }

          /* nested list style */
          ul {
            padding-left: ${PADDING_SM}px !important;
            padding-right: 0;

            ${mq.large} {
              padding-left: ${PADDING_LG}px !important;
              padding-right: 0;
            }
          }

          li {
            list-style: initial;
            line-height: 1.4;
            margin-top: 0.8rem;
          }
        }
      `}
      options={{
        ...props.options,
        overrides: {
          a: MarkdownLink,
          code: SpeakerName,
          blockquote: BodyFootnote,
          iframe: IFrame,
          h2: Heading,
          p: PorDiv,
          img: MarkdownImage,
          Image: MarkdownImage,
          hr: MarkdownTextDivider,
          ...props.options?.overrides,
        },
      }}
    >
      {props.children}
    </Markdown>
  )
}

type SpeakerNameProps = {
  children: React.ReactNode
}

export const SpeakerName: React.FC<SpeakerNameProps> = (props) => {
  return (
    <span
      css={css`
        display: inline-block;
        margin-right: 8px;
        min-width: 55px;
        margin-top: 15px;
        text-align: center;
        background-color: ${colors.gray40};
        border-radius: 15px;
        padding: 3px 9px 1px;
        font-weight: ${font.weight.bold};
        color: ${colors.black30};
        font-size: 13px;
        line-height: 16px;
      `}
    >
      {props.children}
    </span>
  )
}

type BodyFootnoteProps = {
  children: React.ReactNode
}

const BodyFootnote: React.FC<BodyFootnoteProps> = (props) => {
  return (
    <pre
      css={css`
        && {
          margin: 4px 20px 20px;
          white-space: pre-line;
          word-break: break-all;
          font-family: ${font.family};

          ${mq.large} {
            margin-left: 0;
            margin-right: 0;
            ${paddingDesktopStyle};
          }

          p {
            color: ${colors.textLight};
            padding: 0;
            font-size: 12px;
            margin: 0;
            line-height: 1.5;

            & a {
              color: ${colors.link};
              font-weight: ${font.weight.normal};
              border-bottom: none;
              padding-bottom: 0;
              margin: 0;
            }
          }
        }
      `}
    >
      {props.children}
    </pre>
  )
}

export const PorDiv = (props: any) => {
  return props.children.some(
    (node: any) => isImage(node) || isAnchorImage(node)
  ) ? (
    <div
      css={css`
        margin-bottom: 15px;
        & > a {
          border-bottom: none;
          margin: 0;
        }
      `}
      {...props}
    />
  ) : (
    <p {...props} />
  )
}

// NOTE: Drop other attributes (allowtransparency, mozallowfullscreen, ...) to avoid errors from React.
const IFrame: React.FC<{ id?: string; src: string }> = (props) => (
  <iframe
    css={css`
      background-color: transparent;
      overflow: hidden;
      ${iframeCss(props.src)}
    `}
    id={props.id}
    src={props.src}
    frameBorder="0"
    allowFullScreen
  />
)

const iframeCss = (src: string) => {
  if (src.includes('//speakerdeck.com/player')) {
    // for SpeakerDeck
    return css`
      &&&& {
        margin: 20px 5px;
        width: calc(100% - 10px);
        height: calc(100vw * 0.562);

        ${mq.large} {
          margin: 20px 0;
          width: 100%;
          height: calc(770px * 0.562);
        }
      }
    `
  } else {
    // for Google Slide, Datawrapper (charts), etc.
    return css`
      &&&& {
        margin: 20px 0;
        width: 100%;
        height: auto;
        min-height: 350px;
      }
    `
  }
}

type HeadingProps = {
  children: React.ReactNode
}

const Heading: React.FC<HeadingProps> = (props) => (
  <>
    <h2
      css={css`
        position: relative;
      `}
    >
      <span
        id={`${props.children}`}
        css={css`
          position: absolute;
          top: -65px;
        `}
      />
      <span>{props.children}</span>
    </h2>
  </>
)

type IntroFootnoteProps = {
  children: React.ReactNode
}

const IntroFootnote: React.FC<IntroFootnoteProps> = (props) => {
  return (
    <pre
      css={css`
        margin: 4px 0 20px;
        white-space: pre-line;
        word-break: break-all;
        @media (min-width: ${desktopMinWidth}) {
          margin: 4px 0 20px;
        }

        &&&& p {
          color: ${colors.textLight};
          padding: 0;
          font-size: 13px;
          margin-bottom: 0;
          line-height: 20px;
          font-weight: ${font.weight.normal};

          & a.text-link {
            color: ${colors.textLight};
            font-weight: ${font.weight.normal};
            border-bottom: none;
            padding-bottom: 0;
            margin: 0;
            text-decoration: underline;
            text-decoration-style: dotted;
          }
        }
      `}
    >
      {props.children}
    </pre>
  )
}
